import { ReactNode } from "react"
import { ArrowLeft, CalendarDays, ChevronRight, ClipboardList, DoorOpen, MessageCircleQuestion, MessageSquareText, Sparkles, UsersRound, X } from "lucide-react"
import type { ActiveMeeting } from "../types"

export type WorkspaceToolId = "meeting" | "chat" | "questions" | "messages" | "people" | "breakout" | "inspire" | "nexttime" | "polls"

export type WorkspaceTool = {
  id: WorkspaceToolId
  label: string
  description?: string
  badge?: number
  /** Pulsing indicator for something happening in this tool right now (for example a live Inspire Card). */
  live?: boolean
}

export type WorkspaceOverlay = {
  label: string
  /** Goes back to the tools home (or, on phones, closes the drawer). */
  onBack: () => void
  backLabel?: string
}

type WorkspacePanelsProps = {
  /** Tools listed on the panel's home under the meeting details, in order. `meeting` is the home itself. */
  tools: WorkspaceTool[]
  activeTool: WorkspaceToolId
  onToolChange: (tool: WorkspaceToolId) => void
  onClose?: () => void
  compact: boolean
  panels: Partial<Record<WorkspaceToolId, ReactNode>>
  /** Panels reached from a button rather than the tools list (chat, direct messages): they bring their own header. */
  overlays?: Partial<Record<WorkspaceToolId, WorkspaceOverlay>>
}

const TOOL_ICONS: Partial<Record<WorkspaceToolId, ReactNode>> = {
  nexttime: <ClipboardList className="h-4 w-4" />,
  chat: <MessageSquareText className="h-4 w-4" />,
  people: <UsersRound className="h-4 w-4" />,
  questions: <MessageCircleQuestion className="h-4 w-4" />,
  breakout: <DoorOpen className="h-4 w-4" />,
  inspire: <Sparkles className="h-4 w-4" />,
  polls: <ClipboardList className="h-4 w-4" />,
}

const formatSchedule = (startIso?: string | null, endIso?: string | null) => {
  const start = startIso ? new Date(startIso) : null
  const end = endIso ? new Date(endIso) : null
  if (!start || Number.isNaN(start.getTime())) return ""
  const dateLabel = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(start)
  const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" })
  const startLabel = timeFormatter.format(start)
  const endLabel = end && !Number.isNaN(end.getTime()) ? ` – ${timeFormatter.format(end)}` : ""
  return `${dateLabel}, ${startLabel}${endLabel}`
}

export function MeetingDetailsPanel({ meeting, hostName }: { meeting: ActiveMeeting | null; hostName?: string | null }) {
  if (!meeting) return null
  const description = "description" in meeting ? (meeting as any).description : null
  return (
    <div data-testid="call-meeting-details" className="space-y-4 text-sm text-white">
      <div>
        <h3 className="text-base font-semibold">{meeting.title}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-xs text-white/60">
          <CalendarDays className="h-3.5 w-3.5" />
          {formatSchedule(meeting.start_time, meeting.end_time) || "Schedule unavailable"}
        </p>
        {hostName && <p className="mt-1 text-xs text-white/60">Hosted by {hostName}</p>}
      </div>
      {description ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-white/80">{description}</p>
      ) : (
        <p className="text-xs text-white/50">No description was added for this meeting.</p>
      )}
    </div>
  )
}

export function PanelIframe({ title, src, testId }: { title: string; src: string; testId: string }) {
  return (
    <iframe
      title={title}
      src={src}
      data-testid={testId}
      className="h-full w-full border-0 bg-white"
      allow="clipboard-write"
      referrerPolicy="no-referrer"
    />
  )
}

function PanelHeader({ title, onBack, backLabel, liveId, compact, onClose }: { title: string; onBack?: () => void; backLabel?: string; liveId?: string; compact: boolean; onClose?: () => void }) {
  return (
    <div data-testid={onBack ? "call-panel-overlay-header" : "call-panel-home-header"} className="flex h-10 shrink-0 items-center gap-1 border-b border-white/10 px-2">
      {onBack && (
        <button type="button" aria-label={backLabel || "Back to meeting tools"} onClick={onBack} className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}
      <h3 className={`flex min-w-0 flex-1 items-center gap-2 truncate text-sm font-semibold ${onBack ? "" : "px-1.5"}`}>
        {title}
        {liveId && <LiveDot id={liveId} inHeader />}
      </h3>
      {compact && onClose && (
        <button type="button" aria-label="Close meeting tools" onClick={onClose} className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function LiveDot({ id, inHeader }: { id: string; inHeader?: boolean }) {
  return <span data-testid={inHeader ? "call-panel-live" : `call-tool-live-${id}`} className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#f6a35c]" aria-label="Live" />
}

/**
 * WiseFriend side panel for the call. Its home shows the meeting details with the tools listed
 * underneath (People, Questions, Breakout rooms, Inspire Cards); each tool opens in place with a
 * back arrow, so there is never a row of tabs to wrap at narrow widths. Chat and direct messages
 * arrive the same way from the controls bar and the People tool. Every panel stays mounted so
 * iframe state survives switching.
 */
export function WorkspacePanels({ tools, activeTool, onToolChange, onClose, compact, panels, overlays = {} }: WorkspacePanelsProps) {
  const listedTools = tools.filter((tool) => tool.id !== "meeting")
  const overlay = overlays[activeTool]
  const currentTool = listedTools.find((tool) => tool.id === activeTool)
  const home = activeTool === "meeting"
  const panelIds = Array.from(new Set<WorkspaceToolId>([...listedTools.map((tool) => tool.id), ...(Object.keys(overlays) as WorkspaceToolId[])]))
  return (
    <div data-testid="call-workspace-panels" className="flex h-full min-h-0 flex-col bg-[#151d26] text-white">
      {home ? (
        <PanelHeader title="Meeting tools" compact={compact} onClose={onClose} />
      ) : overlay ? (
        <PanelHeader title={overlay.label} onBack={overlay.onBack} backLabel={overlay.backLabel} compact={compact} onClose={onClose} />
      ) : (
        <PanelHeader title={currentTool?.label || ""} onBack={() => onToolChange("meeting")} liveId={currentTool?.live ? currentTool.id : undefined} compact={compact} onClose={onClose} />
      )}
      <div className="relative min-h-0 flex-1">
        <div data-testid="call-tools-home" hidden={!home} className="absolute inset-0 overflow-y-auto">
          <div className="p-4">{panels.meeting}</div>
          {listedTools.length > 0 && (
            <nav aria-label="Meeting tools" className="border-t border-white/10 px-2 py-2">
              <ul className="space-y-0.5">
                {listedTools.map((tool) => (
                  <li key={tool.id}>
                    <button
                      type="button"
                      onClick={() => onToolChange(tool.id)}
                      data-testid={`call-tool-${tool.id}`}
                      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80">{TOOL_ICONS[tool.id]}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          {tool.label}
                          {tool.live && <LiveDot id={tool.id} />}
                        </span>
                        {tool.description && <span className="block truncate text-xs text-white/55">{tool.description}</span>}
                      </span>
                      {tool.badge ? (
                        <span data-testid={`call-tool-badge-${tool.id}`} className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d64545] px-1 text-[10px] font-bold text-white">
                          {tool.badge}
                        </span>
                      ) : null}
                      <ChevronRight className="h-4 w-4 shrink-0 text-white/40" />
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
        {panelIds.map((id) => (
          <div
            key={id}
            role="region"
            aria-label={overlays[id]?.label || listedTools.find((tool) => tool.id === id)?.label}
            data-testid={`call-overlay-${id}`}
            hidden={id !== activeTool}
            className={`absolute inset-0 ${id === "questions" || id === "polls" || id === "breakout" || id === "inspire" || overlays[id] ? "" : "overflow-y-auto p-4"}`}
          >
            {panels[id]}
          </div>
        ))}
      </div>
    </div>
  )
}
